import { Box, Grid, Paper } from "@mui/material";
import clsx from "clsx";
import { PlayerOptions } from "components/SoundClipEditor/SoundClipEditor";
import { observer } from "mobx-react";
import { EddyEffectP2Results } from "models/EddyEffectP2Results";
import React, { useEffect } from "react";
import type {
    ITranscriptionSegment,
    ManualTranscriptRedactionType,
} from "services/TranscriptionService";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "utils/useStore";
import { AcxTranscriptionBody } from "./Components/AcxTranscriptionBody";
import { AcxTranscriptionSidebar } from "./Components/Sidebar";
import { AcxTranscriptionStore } from "./Stores/AcxTranscriptionStore";
import { SpeakerStore } from "./Stores/SpeakerStore/SpeakerStore";
import { TranscriptionSearchResult } from "components/Evaluation/Stores/EvalStore";
import { AdverseEventP2Results } from "models/AdverseEventP2Results";
import type { ConversationSegmentItem } from "services/ConversationService";

interface AcxTranscriptionBaseProps {
    transcriptPhrases?: ITranscriptionSegment[];
    transcriptWords?: ITranscriptionSegment[];
    eddyEffectP2Data: EddyEffectP2Results[];
    adverseEventData: AdverseEventP2Results[];
    focused: boolean;
    onClip?: (start: number, end: number) => Promise<void>;
    hasEvaluation: boolean;
    playClip?: (options: PlayerOptions) => void;
    showTranslationUI?: boolean;
    showUnredactedUI?: boolean;
    translationLanguageCode?: string;
    amdId?: string;
    speakerStore?: SpeakerStore;
    disableRedact?: boolean;
    disableClip?: boolean;
    onChangeLanguage?: (languageCode: string) => void;
    onResultClick?: (result: TranscriptionSearchResult) => void;
    onTranscriptionStoreLoad?: (store: AcxTranscriptionStore) => void;
    onRedactSelection?: (
        redactionType: ManualTranscriptRedactionType,
        selectedChannel?: number,
        selectedText?: string,
        selectedStartTime?: number,
        selectedEndTime?: number,
    ) => Promise<void>;
    conversationSegments?: ConversationSegmentItem[];
}

interface AcxTranscriptionFull extends AcxTranscriptionBaseProps {
    selectedLanguageCode?: string;
    isTranslated?: boolean;
    audioClip?: never;
    minimal?: false;
}

interface AcxTranscriptionMinimal extends AcxTranscriptionBaseProps {
    selectedLanguageCode?: never;
    isTranslated?: never;
    audioClip?: { status: "default" | "success" | "error" | "loading" };
    minimal?: true;
}

type AcxTranscriptionProps = AcxTranscriptionFull | AcxTranscriptionMinimal;

export const AcxTranscription: React.FC<AcxTranscriptionProps> = observer(
    ({
        transcriptPhrases,
        transcriptWords,
        eddyEffectP2Data,
        onClip,
        playClip,
        showTranslationUI,
        showUnredactedUI,
        translationLanguageCode,
        isTranslated,
        onChangeLanguage,
        selectedLanguageCode,
        hasEvaluation,
        audioClip,
        minimal,
        amdId,
        speakerStore,
        disableClip,
        disableRedact,
        focused,
        onResultClick,
        adverseEventData,
        onTranscriptionStoreLoad,
        onRedactSelection,
        conversationSegments,
    }) => {
        const store = useStore(AcxTranscriptionStore);
        const authstore = useStore(AuthStore);

        if (!authstore.canUserView("Transcription")) {
            return null;
        }

        useEffect(() => {
            store.setPhrases(transcriptPhrases);
            store.setWords(transcriptWords);
            store.setConversationSegments(conversationSegments);
        }, [store, transcriptPhrases, transcriptWords, conversationSegments]);

        useEffect(() => {
            store.setEddyEffect(eddyEffectP2Data);
            store.setHasEval(hasEvaluation);
            store.setFocused(focused);
            store.setPlayClip(playClip);
            store.setAdverseEventData(adverseEventData);
            if (onTranscriptionStoreLoad) {
                onTranscriptionStoreLoad(store);
            }
        }, [
            eddyEffectP2Data,
            hasEvaluation,
            store,
            focused,
            playClip,
            adverseEventData,
            onTranscriptionStoreLoad,
        ]);

        const handleChecked = (checked: boolean) => {
            store.isCheckedEddyEffectP2 = checked;
            store.searchString = "";
        };

        useEffect(() => {
            speakerStore?.setAmdId(amdId);
        }, [amdId, speakerStore]);

        if (minimal) {
            return (
                <AcxTranscriptionBody
                    hasEvaluation={hasEvaluation}
                    playerBlurb={playClip}
                    audioClip={audioClip}
                    onClip={onClip}
                    speakerStore={speakerStore}
                    disableClip={disableClip}
                    disableRedact={disableRedact}
                    onRedactSelection={onRedactSelection}
                />
            );
        }

        return (
            <Paper square sx={{ height: "100%", minWidth: "141px" }}>
                <Grid
                    item
                    xs={12}
                    style={{ display: "flex", height: "100%" }}
                    className={clsx("pendo-ignore")}
                >
                    <Grid
                        item
                        xs={2}
                        style={{
                            marginLeft: "1%",
                            overflowY: "auto",
                        }}
                    >
                        <AcxTranscriptionSidebar
                            speakerStore={speakerStore}
                            showTranslationUI={showTranslationUI}
                            showUnredactedUI={showUnredactedUI}
                            translationLanguageCode={translationLanguageCode}
                            handleChecked={handleChecked}
                            selectedLanguageCode={selectedLanguageCode}
                            onChangeLanguage={onChangeLanguage}
                            onResultClick={onResultClick}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={10}
                        style={{
                            marginLeft: "2%",
                        }}
                    >
                        {isTranslated && (
                            <Grid
                                container
                                item
                                xs={12}
                                style={{ position: "sticky" }}
                            >
                                <Box
                                    fontFamily={"Inter"}
                                    fontStyle={"italic"}
                                    fontWeight={"bold"}
                                    style={{
                                        color: "gray",
                                    }}
                                >
                                    Translated transcript
                                </Box>
                            </Grid>
                        )}
                        <Box style={{ overflow: "auto", height: "100%" }}>
                            <AcxTranscriptionBody
                                hasEvaluation={hasEvaluation}
                                playerBlurb={playClip}
                                onClip={onClip}
                                speakerStore={speakerStore}
                                disableClip={disableClip}
                                disableRedact={disableRedact}
                                onRedactSelection={onRedactSelection}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
        );
    },
);
